import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { agentsApi } from "@/api/agents";
import { adminKeys } from "@/hooks/useAdmin";
import type {
  AgentExamPayload,
  AgentLicensing,
  AgentOnboardingItemPayload,
  OnboardingItemKey,
} from "@/types/agent";

export const agentKeys = {
  all: ["agents"] as const,
  selectList: () => [...agentKeys.all, "select"] as const,
  licensing: (agentId: string) =>
    [...agentKeys.all, agentId, "licensing"] as const,
  onboarding: (agentId: string) =>
    [...agentKeys.all, agentId, "onboarding"] as const,
};

export function agentsForSelectQueryOptions() {
  return queryOptions({
    queryKey: agentKeys.selectList(),
    queryFn: () => agentsApi.getAgentsForSelect(),
    staleTime: 10 * 60_000,
  });
}

export function useAgentsForSelect() {
  return useQuery(agentsForSelectQueryOptions());
}

export function useAgentLicensing(agentId: string) {
  return useQuery({
    queryKey: agentKeys.licensing(agentId),
    queryFn: () => agentsApi.getLicensing(agentId),
    staleTime: 30_000,
  });
}

/** Every licensing change answers with the fresh state; the list shows a summary of it. */
function useLicensingMutation<TVariables>(
  agentId: string,
  mutationFn: (variables: TVariables) => Promise<AgentLicensing>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (licensing) => {
      queryClient.setQueryData(agentKeys.licensing(agentId), licensing);
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
      // The checklist hints come from the authorizations and the exam.
      queryClient.invalidateQueries({
        queryKey: agentKeys.onboarding(agentId),
      });
    },
  });
}

export function useSetAgentAuthorizations(agentId: string) {
  return useLicensingMutation(agentId, (insurerIds: string[]) =>
    agentsApi.setAuthorizations(agentId, insurerIds),
  );
}

export function useSetAgentExam(agentId: string) {
  return useLicensingMutation(agentId, (payload: AgentExamPayload) =>
    agentsApi.setExam(agentId, payload),
  );
}

export function useClearAgentExam(agentId: string) {
  return useLicensingMutation(agentId, (insurerId: string) =>
    agentsApi.clearExam(agentId, insurerId),
  );
}

export function useAgentOnboarding(agentId: string) {
  return useQuery({
    queryKey: agentKeys.onboarding(agentId),
    queryFn: () => agentsApi.getOnboarding(agentId),
    staleTime: 30_000,
  });
}

export function useSetAgentOnboardingItem(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      payload,
    }: {
      key: OnboardingItemKey;
      payload: AgentOnboardingItemPayload;
    }) => agentsApi.setOnboardingItem(agentId, key, payload),
    onSuccess: (onboarding) => {
      queryClient.setQueryData(agentKeys.onboarding(agentId), onboarding);
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}
